import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { IMG } from "../lib/constants";
import { StorePhoto, STORE_PHOTOS, storePhotoUrl } from "../components/StorePhoto";

export default function About() {
    return (
        <>
            <SEO
                title="Our Story"
                description="The story behind Liquor Studio — Clinton, Missouri's curated spirits destination. Locally owned, expertly staffed, built for Henry County and Truman Lake."
                path="/about"
            />

            {/* Header */}
            <section
                className="relative"
                style={{
                    paddingTop: 160,
                    paddingBottom: 64,
                    borderBottom: "1px solid var(--ls-border)",
                }}
                data-testid="about-header"
            >
                <div className="ls-container">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl"
                    >
                        <span className="eyebrow">Our Story</span>
                        <h1
                            className="font-display mt-5 text-white"
                            style={{
                                fontSize: "clamp(44px, 7vw, 84px)",
                                fontWeight: 500,
                                lineHeight: 1.02,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            We didn't open a liquor store.<br />
                            <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>
                                We opened a studio.
                            </em>
                        </h1>
                    </motion.div>
                </div>
            </section>

            {/* Narrative */}
            <section className="section-y" data-testid="about-narrative">
                <div className="ls-container">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
                        <div className="lg:col-span-7">
                            <div
                                className="font-body"
                                style={{
                                    color: "var(--ls-text-secondary)",
                                    fontSize: 18,
                                    lineHeight: 1.8,
                                }}
                            >
                                <p className="mb-6">
                                    Clinton, Missouri is a town that knows how to host. Truman Lake
                                    weekenders pour through every Friday. Henry County families
                                    gather for Sunday dinners, anniversary dinners, just-because
                                    dinners. The square hums at 5 PM. And for a long time, the only
                                    thing missing was a liquor store that respected all of it.
                                </p>
                                <p className="mb-6">
                                    Liquor Studio opened to fix that. We wanted a place where the
                                    bourbon shelf isn't an afterthought — it's the headline. Where
                                    the wine section isn't sorted by price tag, but by region and
                                    intent. Where you can ask, "I'm grilling tri-tip tonight, what
                                    am I drinking?" and the person at the counter actually answers.
                                </p>
                                <p className="mb-6">
                                    The name was deliberate. A studio is where things are made
                                    carefully. Curated. Considered. We taste what we carry. We talk
                                    to distillers. We pick allocated single barrels for our shelves
                                    that you genuinely can't find anywhere else in Henry County. And
                                    when something new comes in, we tell you about it — because
                                    that's the whole point.
                                </p>
                                <p className="mb-6">
                                    For Truman Lake weekenders, we built the cooler aisle of your
                                    dreams. Local Missouri craft beer next to imports next to seltzers
                                    next to chilled wine you can pour straight onto the boat. We
                                    keep the parking lot easy. We're two minutes off MO-7 and MO-13.
                                    And we're open late on Fridays and Saturdays because we know
                                    what time you actually leave.
                                </p>
                                <p className="mb-6">
                                    For Clinton locals, this is your store. Your bourbon. Your
                                    wedding-toast champagne. Your weekday Manhattan. We card anyone
                                    who looks under 40, because we take being a licensed Missouri
                                    retailer seriously — and we want you to be able to relax knowing
                                    everything here is the way it should be.
                                </p>
                                <p>
                                    We're not the biggest. We're the most curated. Welcome to
                                    Liquor Studio.
                                </p>
                            </div>

                            <Link to="/visit" className="btn-outline-gold mt-10" data-testid="about-visit-cta">
                                Come Say Hi <ArrowRight size={16} />
                            </Link>
                        </div>

                        <div className="lg:col-span-5 flex flex-col gap-6">
                            <StorePhoto
                                slug="entrance-stone-wall"
                                style={{ aspectRatio: "4/5", borderRadius: 6, border: "1px solid var(--ls-border-strong)" }}
                                testid="about-entrance"
                            />
                            <div className="grid grid-cols-2 gap-6">
                                <StorePhoto
                                    slug="wine-aisle"
                                    style={{ aspectRatio: "1/1", borderRadius: 6, border: "1px solid var(--ls-border-strong)" }}
                                    testid="about-wine-aisle"
                                />
                                <StorePhoto
                                    slug="liqueur-aisle"
                                    style={{ aspectRatio: "1/1", borderRadius: 6, border: "1px solid var(--ls-border-strong)" }}
                                    testid="about-liqueur-aisle"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* INSIDE THE STUDIO — 6-photo gallery */}
            <section
                aria-label="Inside the Studio"
                data-testid="about-inside-studio"
                style={{ background: "#070707", borderTop: "1px solid var(--ls-border)", borderBottom: "1px solid var(--ls-border)", padding: "96px 0" }}
            >
                <div className="ls-container">
                    <div className="max-w-3xl mb-12">
                        <span className="eyebrow">Inside the Studio</span>
                        <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(34px, 4.5vw, 56px)", fontWeight: 500, lineHeight: 1.05, letterSpacing: "-0.02em" }}>
                            Six photos. <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>The whole story.</em>
                        </h2>
                        <p className="mt-4" style={{ color: "var(--ls-text-secondary)", fontSize: 17, lineHeight: 1.7 }}>
                            This is what you'll see when you walk in. No staging, no filters — just the store.
                        </p>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
                        {["storefront-night", "entrance-stone-wall", "wine-aisle", "beer-cave", "liqueur-aisle", "allocated-weller-bottle"].map((slug, i) => (
                            <figure
                                key={slug}
                                style={{ margin: 0, background: "var(--ls-panel)", border: "1px solid var(--ls-border)", borderRadius: 6, overflow: "hidden", display: "flex", flexDirection: "column" }}
                                data-testid={`inside-studio-${slug}`}
                            >
                                <StorePhoto slug={slug} loading="lazy" style={{ aspectRatio: "4/3" }} />
                                <figcaption style={{ padding: "16px 18px", flex: 1, display: "flex", flexDirection: "column", gap: 6, background: "var(--ls-panel)" }}>
                                    <span className="eyebrow" style={{ fontSize: 10, letterSpacing: "0.22em", color: "var(--ls-gold)" }}>{STORE_PHOTOS[slug].credit}</span>
                                    <p style={{ fontSize: 14, lineHeight: 1.55, color: "var(--ls-text)" }}>{STORE_PHOTOS[slug].caption}</p>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* Values */}
            <section
                style={{
                    background: "#070707",
                    borderTop: "1px solid var(--ls-border)",
                    borderBottom: "1px solid var(--ls-border)",
                }}
                data-testid="about-values"
            >
                <div className="ls-container py-20">
                    <span className="eyebrow">What We Stand For</span>
                    <h2
                        className="font-display mt-4 text-white"
                        style={{
                            fontSize: "clamp(34px, 4.5vw, 52px)",
                            fontWeight: 500,
                            lineHeight: 1.1,
                            letterSpacing: "-0.02em",
                            maxWidth: 800,
                        }}
                    >
                        Four things we'll never compromise on.
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-14">
                        {[
                            { n: "01", t: "Curation", b: "We taste it before you do." },
                            { n: "02", t: "Knowledge", b: "Ask anything. Real answers, not Google." },
                            { n: "03", t: "Local", b: "Missouri brewers and distillers up front." },
                            { n: "04", t: "Responsibility", b: "We ID. We care. We're licensed." },
                        ].map((v) => (
                            <div key={v.n}>
                                <span className="font-bebas" style={{ fontSize: 48, color: "var(--ls-gold)", lineHeight: 1 }}>
                                    {v.n}
                                </span>
                                <h3 className="font-display mt-3 text-white" style={{ fontSize: 24, fontWeight: 500 }}>
                                    {v.t}
                                </h3>
                                <p className="mt-2 text-sm" style={{ color: "var(--ls-text-secondary)" }}>
                                    {v.b}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
